import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Container, Row, Col, Tabs, Tab, Button, ProgressBar, Spinner, Alert } from 'react-bootstrap';
import styles from './ContractRegistration.module.css';
import DadosContratoTab from './Tabs/DadosContratoTab';
import ContratanteTab from './Tabs/ContratanteTab';
import RepresentanteTab from './Tabs/RepresentanteTab';
import ClausulasTab from './Tabs/ClausulasTab';
import api from '../../services/api';
import { notify } from '../../stores/useNotificationStore';
import { useInterfaceStore } from '../../stores/useInterfaceStore';
import { FiArrowLeft, FiSave } from '../../utils/icons';

export const validateCNPJ = (cnpj: string): boolean => {
  const n = cnpj.replace(/\D/g, '');
  if (n.length !== 14 || /^(\d)\1{13}$/.test(n)) return false;
  let len = 12, nums = n.substring(0, len), digits = n.substring(len);
  let sum = 0, pos = 5;
  for (let i = 0; i < 12; i++) { sum += parseInt(nums[i]) * pos; pos = pos === 2 ? 9 : pos - 1; }
  let r = sum % 11; let d1 = r < 2 ? 0 : 11 - r;
  if (parseInt(digits[0]) !== d1) return false;
  len = 13; nums = n.substring(0, len); sum = 0; pos = 6;
  for (let i = 0; i < 13; i++) { sum += parseInt(nums[i]) * pos; pos = pos === 2 ? 9 : pos - 1; }
  r = sum % 11; let d2 = r < 2 ? 0 : 11 - r;
  return parseInt(digits[1]) === d2;
};

export interface IContratoData {
  id?: number;
  razao_social: string;
  nome_fantasia: string;
  cnpj: string;
  ramos_atuacao: string;
  ramo_escopo: string;
  tipo_agente: string;
  dpo: string;
  escopo_descricao: string;
  vigencia_inicio: string;
  vigencia_fim: string;
  contrato_arquivo?: string;
}

export interface IPessoa {
  nome: string;
  email: string;
  telefone: string;
}

export interface IClausulas {
  clausulas_privacidade: boolean;
  clausulas_seguranca: boolean;
  nda: boolean;
  nda_arquivo?: string;
  adequado_lgpd: boolean;
}

const TABS = ['dados', 'contratante', 'representante', 'clausulas'] as const;
type TabKey = typeof TABS[number];

const TAB_TITLES: Record<TabKey, string> = {
  dados: 'Dados do Contrato',
  contratante: 'Contratante Principal',
  representante: 'Representante da Contratada',
  clausulas: 'Cláusulas e Conformidade',
};

const ContractRegistration: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabKey>('dados');
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isViewMode = location.pathname.includes('/visualizar/');
  const { empresaId } = useInterfaceStore();

  const [contrato, setContrato] = useState<IContratoData>({
    razao_social: '', nome_fantasia: '', cnpj: '', ramos_atuacao: '',
    ramo_escopo: '', tipo_agente: '', dpo: '', escopo_descricao: '',
    vigencia_inicio: '', vigencia_fim: '',
  });
  const [contratante, setContratante] = useState<IPessoa>({ nome: '', email: '', telefone: '' });
  const [representante, setRepresentante] = useState<IPessoa>({ nome: '', email: '', telefone: '' });
  const [clausulas, setClausulas] = useState<IClausulas>({
    clausulas_privacidade: false, clausulas_seguranca: false,
    nda: false, adequado_lgpd: false,
  });

  const [contratoFile, setContratoFile] = useState<File | null>(null);
  const [ndaFile, setNdaFile] = useState<File | null>(null);
  const [cnpjError, setCnpjError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!id) return;
    const fetch = async () => {
      const res = await api.getContrato(Number(id));
      if (res.success && res.data) {
        const d = res.data;
        setContrato({
          id: d.id, razao_social: d.razao_social || '', nome_fantasia: d.nome_fantasia || '',
          cnpj: d.cnpj || '', ramos_atuacao: d.ramos_atuacao || '', ramo_escopo: d.ramo_escopo || '',
          tipo_agente: d.tipo_agente || '', dpo: d.dpo || '', escopo_descricao: d.escopo_descricao || '',
          vigencia_inicio: d.vigencia_inicio || '', vigencia_fim: d.vigencia_fim || '',
          contrato_arquivo: d.contrato_arquivo,
        });
        setContratante({ nome: d.contratante_nome || '', email: d.contratante_email || '', telefone: d.contratante_telefone || '' });
        setRepresentante({ nome: d.representante_nome || '', email: d.representante_email || '', telefone: d.representante_telefone || '' });
        setClausulas({
          clausulas_privacidade: !!d.clausulas_privacidade, clausulas_seguranca: !!d.clausulas_seguranca,
          nda: !!d.nda, nda_arquivo: d.nda_arquivo, adequado_lgpd: !!d.adequado_lgpd,
        });
      }
    };
    fetch();
  }, [id]);

  const handleSubmit = async () => {
    if (!contrato.razao_social.trim()) { notify.warning('Razão Social é obrigatória.'); return; }
    if (!validateCNPJ(contrato.cnpj)) { setCnpjError('CNPJ inválido.'); setActiveTab('dados'); return; }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      const payload = {
        ...contrato,
        contratante_nome: contratante.nome, contratante_email: contratante.email, contratante_telefone: contratante.telefone,
        representante_nome: representante.nome, representante_email: representante.email, representante_telefone: representante.telefone,
        clausulas_privacidade: clausulas.clausulas_privacidade, clausulas_seguranca: clausulas.clausulas_seguranca,
        nda: clausulas.nda, adequado_lgpd: clausulas.adequado_lgpd,
      };
      formData.append('data', JSON.stringify(payload));
      if (contratoFile) formData.append('contrato', contratoFile);
      if (ndaFile) formData.append('nda', ndaFile);

      const isEditing = !!id;
      const res = isEditing
        ? await api.updateContrato(Number(id), formData)
        : await api.createContrato(formData);

      if (res.success) {
        notify.success(isEditing ? 'Fornecedor atualizado com sucesso!' : 'Fornecedor cadastrado com sucesso!');
        navigate('/app/contratos/list');
      } else {
        notify.error(`Erro ao salvar: ${res.error || ''}`);
      }
    } catch {
      notify.error('Erro de conexão.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const stepMap: Record<TabKey, number> = { dados: 1, contratante: 2, representante: 3, clausulas: 4 };
  const currentStep = stepMap[activeTab];
  const progress = (currentStep / 4) * 100;

  return (
    <Container fluid className={styles.container}>
      <Row className={styles.header}>
        <Col>
          <h1 className={styles.title}>{isViewMode ? 'Visualização de Fornecedor' : (id ? 'Editar Fornecedor' : 'Cadastro de Fornecedores')}</h1>
          {!isViewMode && (
            <>
              <p className={styles.progressText}>Passo {currentStep} de 4</p>
              <ProgressBar now={progress} label={`${Math.round(progress)}%`} />
            </>
          )}
        </Col>
      </Row>
      {isViewMode && (
        <Alert variant="info" className="mb-3">
          Modo somente leitura. Para editar, acesse a opção "Editar" na listagem de contratos.
        </Alert>
      )}
      <fieldset disabled={isViewMode}>
        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab((k as TabKey) || 'dados')}
          id="contract-registration-tabs"
          className="mb-3"
        >
          <Tab eventKey="dados" title={TAB_TITLES.dados}>
            <DadosContratoTab
              data={contrato}
              setData={setContrato}
              cnpjError={cnpjError}
              setCnpjError={setCnpjError}
              onContratoFileChange={setContratoFile}
              contratoFileName={contratoFile?.name || contrato.contrato_arquivo || ''}
              contratoId={contrato.id}
              showDownload={!contratoFile && !!contrato.contrato_arquivo}
            />
          </Tab>
          <Tab eventKey="contratante" title={TAB_TITLES.contratante}>
            <ContratanteTab contratante={contratante} setContratante={setContratante} />
          </Tab>
          <Tab eventKey="representante" title={TAB_TITLES.representante}>
            <RepresentanteTab representante={representante} setRepresentante={setRepresentante} />
          </Tab>
          <Tab eventKey="clausulas" title={TAB_TITLES.clausulas}>
            <ClausulasTab
              clausulas={clausulas}
              setClausulas={setClausulas}
              onNdaFileChange={setNdaFile}
              ndaFileName={ndaFile?.name || clausulas.nda_arquivo || ''}
              contratoId={contrato.id}
              showNdaDownload={!ndaFile && !!clausulas.nda_arquivo}
              empresaId={empresaId}
            />
          </Tab>
        </Tabs>
      </fieldset>
      <Row>
        <Col className={styles.buttonContainer}>
          {isViewMode ? (
            <Button variant="secondary" onClick={() => navigate('/app/contratos/list')}>
              <FiArrowLeft /> Voltar para Listagem
            </Button>
          ) : (
            <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? <Spinner as="span" animation="border" size="sm" /> : <><FiSave /> Salvar</>}
            </Button>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default ContractRegistration;
